import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import cls from "./AdminGameQuestionsPage.module.scss";
import {
  listAdminGameQuestions,
  updateAdminGameQuestionStatus,
} from "../../../features/game/gameApi";

export default function AdminGameQuestionsPage() {
  const [questions, setQuestions] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [togglingId, setTogglingId] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    roundNumber: "",
    answeringGender: "",
  });

  const load = async (nextPage = page, nextFilters = filters) => {
    const params = {
      page: nextPage,
      ...(nextFilters.roundNumber
        ? { roundNumber: Number(nextFilters.roundNumber) }
        : {}),
      ...(nextFilters.answeringGender
        ? { answeringGender: nextFilters.answeringGender }
        : {}),
    };

    const data = await listAdminGameQuestions(params);
    setQuestions(data.questions || []);
    setTotalPages(data.totalPages || 1);
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const data = await listAdminGameQuestions({ page: 1 });
        if (mounted) {
          setQuestions(data.questions || []);
          setTotalPages(data.totalPages || 1);
        }
      } finally {
        if (mounted) setListLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const toggleStatus = async (question) => {
    setTogglingId(question._id);
    try {
      await updateAdminGameQuestionStatus(question._id, !question.isActive);
      await load();
    } finally {
      setTogglingId("");
    }
  };

  const stats = useMemo(() => {
    const total = questions.length;
    const active = questions.filter((q) => q.isActive).length;
    const inactive = total - active;
    const male = questions.filter((q) => q.answeringGender === "male").length;
    const female = questions.filter(
      (q) => q.answeringGender === "female",
    ).length;

    return { total, active, inactive, male, female };
  }, [questions]);

  const applyFilters = async (e) => {
    e.preventDefault();
    const newPage = 1;
    setPage(newPage);
    await load(newPage, filters);
  };

  const resetFilters = async () => {
    const next = {
      roundNumber: "",
      answeringGender: "",
    };
    setFilters(next);
    setPage(1);
    await load(1, next);
  };

  return (
    <div className={cls.page}>
      <div className={cls.bgGlowBlue} />
      <div className={cls.bgGlowPink} />
      <div className={cls.gridLines} />

      <div className={cls.container}>
        <header className={cls.hero}>
          <div className={cls.heroCopy}>
            <span className={cls.kicker}>ADMIN · GAME MANAGEMENT</span>
            <h1 className={cls.title}>Game Questions</h1>
            <p className={cls.subtitle}>
              Manage your question bank, filter by round and answering gender,
              and quickly activate or deactivate questions.
            </p>
          </div>

          <div className={cls.heroSide}>
            <Link className={cls.primaryBtn} to="/admin/game-questions/add">
              <span className={cls.btnGlow} />
              <span>Add Question</span>
            </Link>
          </div>
        </header>

        <section className={cls.statGrid}>
          <div className={cls.statCard}>
            <div className={cls.statTop}>
              <span className={cls.statDot} />
              <span className={cls.statLabel}>Loaded</span>
            </div>
            <div className={cls.statValue}>{stats.total}</div>
            <div className={cls.statMeta}>Questions on this page</div>
          </div>

          <div className={cls.statCard}>
            <div className={cls.statTop}>
              <span className={`${cls.statDot} ${cls.activeDot}`} />
              <span className={cls.statLabel}>Active</span>
            </div>
            <div className={cls.statValue}>{stats.active}</div>
            <div className={cls.statMeta}>Currently enabled</div>
          </div>

          <div className={cls.statCard}>
            <div className={cls.statTop}>
              <span className={`${cls.statDot} ${cls.warningDot}`} />
              <span className={cls.statLabel}>Inactive</span>
            </div>
            <div className={cls.statValue}>{stats.inactive}</div>
            <div className={cls.statMeta}>Currently disabled</div>
          </div>

          <div className={cls.statCard}>
            <div className={cls.statTop}>
              <span className={`${cls.statDot} ${cls.pinkDot}`} />
              <span className={cls.statLabel}>Answering Mix</span>
            </div>
            <div className={cls.statSplit}>
              <div>
                <strong>{stats.male}</strong>
                <span>Male</span>
              </div>
              <div>
                <strong>{stats.female}</strong>
                <span>Female</span>
              </div>
            </div>
          </div>
        </section>

        <section className={cls.panel}>
          <div className={cls.panelHead}>
            <div>
              <h2 className={cls.panelTitle}>Question Filters</h2>
              <p className={cls.panelSub}>
                Narrow results by round number and answering gender.
              </p>
            </div>
          </div>

          <form className={cls.filterBar} onSubmit={applyFilters}>
            <div className={cls.field}>
              <label className={cls.fieldLabel}>Round Number</label>
              <input
                className={cls.filterInput}
                type="number"
                min="1"
                max="50"
                placeholder="Enter round"
                value={filters.roundNumber}
                onChange={(e) =>
                  setFilters((p) => ({ ...p, roundNumber: e.target.value }))
                }
              />
            </div>

            <div className={cls.field}>
              <label className={cls.fieldLabel}>Answering Gender</label>
              <select
                className={cls.filterInput}
                value={filters.answeringGender}
                onChange={(e) =>
                  setFilters((p) => ({
                    ...p,
                    answeringGender: e.target.value,
                  }))
                }
              >
                <option value="">All Answering Genders</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div className={cls.filterActions}>
              <button className={cls.primaryBtn} type="submit">
                <span className={cls.btnGlow} />
                <span>Apply Filters</span>
              </button>

              <button
                className={cls.secondaryBtn}
                type="button"
                onClick={resetFilters}
              >
                Reset
              </button>
            </div>
          </form>
        </section>

        <section className={cls.panel}>
          <div className={cls.panelHead}>
            <div>
              <h2 className={cls.panelTitle}>Question Library</h2>
              <p className={cls.panelSub}>
                Showing up to 50 questions per page.
              </p>
            </div>

            <div className={cls.pageChip}>
              Page {page} / {totalPages}
            </div>
          </div>

          {listLoading ? (
            <div className={cls.stateCard}>
              <div className={cls.loader} />
              <h3>Loading questions</h3>
              <p>Please wait while we fetch the latest records.</p>
            </div>
          ) : questions.length === 0 ? (
            <div className={cls.stateCard}>
              <div className={cls.emptyIcon}>?</div>
              <h3>No questions found</h3>
              <p>Try changing the filters or add a new question.</p>
            </div>
          ) : (
            <>
              <div className={cls.desktopTableWrap}>
                <table className={cls.table}>
                  <thead>
                    <tr>
                      <th>Round</th>
                      <th>Question</th>
                      <th>Answering</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>

                  <tbody>
                    {questions.map((q) => (
                      <tr key={q._id}>
                        <td>
                          <span className={cls.roundBadge}>
                            R{q.roundNumber}
                          </span>
                        </td>

                        <td>
                          <div className={cls.questionCell}>
                            <div className={cls.questionTitle}>{q.title}</div>
                          </div>
                        </td>

                        <td>
                          <span className={cls.genderTag}>
                            {q.answeringGender}
                          </span>
                        </td>

                        <td>
                          <span
                            className={`${cls.statusBadge} ${
                              q.isActive ? cls.active : cls.inactive
                            }`}
                          >
                            {q.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>

                        <td>
                          <div className={cls.tableActions}>
                            <Link
                              className={cls.viewBtn}
                              to={`/admin/game-questions/${q._id}`}
                            >
                              View
                            </Link>

                            <button
                              type="button"
                              className={cls.toggleBtn}
                              onClick={() => toggleStatus(q)}
                              disabled={togglingId === q._id}
                            >
                              {togglingId === q._id
                                ? "Updating..."
                                : q.isActive
                                  ? "Deactivate"
                                  : "Activate"}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className={cls.mobileList}>
                {questions.map((q) => (
                  <article className={cls.questionCard} key={q._id}>
                    <div className={cls.cardTop}>
                      <span className={cls.roundBadge}>
                        Round {q.roundNumber}
                      </span>
                      <span
                        className={`${cls.statusBadge} ${
                          q.isActive ? cls.active : cls.inactive
                        }`}
                      >
                        {q.isActive ? "Active" : "Inactive"}
                      </span>
                    </div>

                    <div>
                      <h3 className={cls.cardTitle}>{q.title}</h3>
                    </div>

                    <div className={cls.metaGrid}>
                      <div className={cls.metaItem}>
                        <span className={cls.metaLabel}>Answering</span>
                        <span className={cls.metaValue}>
                          {q.answeringGender}
                        </span>
                      </div>

                      <div className={cls.metaItem}>
                        <span className={cls.metaLabel}>Round</span>
                        <span className={cls.metaValue}>{q.roundNumber}</span>
                      </div>
                    </div>

                    <div className={cls.tableActions}>
                      <Link
                        className={cls.viewBtn}
                        to={`/admin/game-questions/${q._id}`}
                      >
                        View
                      </Link>

                      <button
                        type="button"
                        className={cls.toggleBtn}
                        onClick={() => toggleStatus(q)}
                        disabled={togglingId === q._id}
                      >
                        {togglingId === q._id
                          ? "Updating..."
                          : q.isActive
                            ? "Deactivate"
                            : "Activate"}
                      </button>
                    </div>
                  </article>
                ))}
              </div>

              <div className={cls.pagination}>
                <button
                  className={cls.secondaryBtn}
                  type="button"
                  disabled={page <= 1}
                  onClick={async () => {
                    const p = page - 1;
                    setPage(p);
                    await load(p);
                  }}
                >
                  Prev
                </button>

                <div className={cls.paginationInfo}>
                  <span className={cls.paginationCurrent}>{page}</span>
                  <span className={cls.paginationText}>of {totalPages}</span>
                </div>

                <button
                  className={cls.secondaryBtn}
                  type="button"
                  disabled={page >= totalPages}
                  onClick={async () => {
                    const p = page + 1;
                    setPage(p);
                    await load(p);
                  }}
                >
                  Next
                </button>
              </div>
            </>
          )}
        </section>
      </div>
    </div>
  );
}
